import type { ReactNode } from 'react'
import type { CommentKind } from '../store'
import { useCoachingStore } from '../store'
import { KIND_ACCENT } from './comment-card'
import { EMPTY_OVERALL_DRAFT } from './review-ui-state'

const KINDS: CommentKind[] = ['praise', 'critique', 'suggestion']
const KIND_LABEL: Record<CommentKind, string> = {
  praise: 'Praise',
  critique: 'Critique',
  suggestion: 'Suggestion',
}

/**
 * Overall-comment block (spec §8.2), one per expanded review, bottom of the
 * shared sections (§8.4). Dashed border marks it as always-open/uncommitted
 * (spec §14 token conventions), unlike the transient floating Composer.
 *
 * `submissionId` keys the draft, and posting targets the slice's `expandedId`
 * (the `post` action's existing contract, owned by A1 and not modified here) —
 * this component is only ever rendered inside the currently expanded
 * submission's body, so the two are always the same id in practice.
 *
 * The draft lives on the slice rather than in this component because this
 * component is unmounted by things that are not the coach abandoning it:
 * folding the week the review sits in takes the whole expanded card off screen
 * (LG-642). Keyed by submission, a restored draft can only ever come back
 * under the submission it was typed about.
 */
export function OverallComment(props: {
  submissionId: string
  sectionKey?: string
}): ReactNode {
  const { submissionId, sectionKey = 'overall' } = props
  const { post, overallDrafts, setOverallDraft } = useCoachingStore().review
  const { kind, text } = overallDrafts[submissionId] ?? EMPTY_OVERALL_DRAFT

  const setKind = (next: CommentKind) =>
    setOverallDraft(submissionId, { kind: next, text })
  const setText = (next: string) =>
    setOverallDraft(submissionId, { kind, text: next })

  const canPost = text.trim().length > 0

  const handlePost = () => {
    if (!canPost) return
    post({ kind, section: sectionKey, target: 'Overall', body: text.trim() })
    setOverallDraft(submissionId, { kind, text: '' })
  }

  return (
    <div
      data-submission-id={submissionId}
      className="rounded-xl border border-dashed border-(--cd-border-3) bg-(--cd-card) p-4"
    >
      <div className="mb-2.5 text-[11.5px] font-bold tracking-wide text-(--cd-text-muted) uppercase">
        Overall comment
      </div>

      <div className="mb-2.5 flex gap-1.5">
        {KINDS.map((k) => {
          const active = kind === k
          const accent = KIND_ACCENT[k]
          return (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className="rounded-full border px-3 py-1 text-xs font-semibold"
              style={
                active
                  ? {
                      background: accent.pillBg,
                      color: accent.pillFg,
                      borderColor: accent.pillFg,
                    }
                  : {
                      background: 'transparent',
                      color: 'var(--cd-text-faint)',
                      borderColor: 'var(--cd-border-2)',
                    }
              }
            >
              {KIND_LABEL[k]}
            </button>
          )
        })}
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Wrap-up feedback on the whole submission…"
        rows={3}
        className="w-full resize-y rounded-lg border border-(--cd-border-2) bg-(--cd-bg) px-3 py-2.5 text-[13px] text-(--cd-text) outline-none"
      />

      <div className="mt-2.5 flex justify-end">
        <button
          type="button"
          disabled={!canPost}
          onClick={handlePost}
          className="rounded-lg bg-(--cd-green) px-4 py-1.5 text-[13px] font-semibold text-(--cd-bg) disabled:cursor-not-allowed"
          style={{ opacity: canPost ? 1 : 0.45 }}
        >
          Post comment
        </button>
      </div>
    </div>
  )
}
